import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCities } from '../utils/festivalApi';

function slugifyCity(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const SCROLL_SPEED_PX_PER_SEC = 42;
const RESUME_DELAY_MS = 1200;

export default function CityMarquee() {
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const isPausedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastXRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetchCities()
      .then((data) => {
        if (cancelled) return;
        setCities(data);
      })
      .catch(() => {
        setCities([]);
      })
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track || cities.length === 0) return;

    let lastTime = performance.now();
    const loop = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!isDraggingRef.current && !isPausedRef.current) {
        const maxScroll = track.scrollWidth / 2;
        container.scrollLeft += SCROLL_SPEED_PX_PER_SEC * dt;
        if (container.scrollLeft >= maxScroll) {
          container.scrollLeft -= maxScroll;
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cities.length]);

  const pauseAutoScroll = () => {
    isPausedRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  };

  const scheduleResume = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, RESUME_DELAY_MS);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true;
    lastXRef.current = e.clientX;
    pauseAutoScroll();
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    const dx = lastXRef.current - e.clientX;
    containerRef.current.scrollLeft += dx;
    lastXRef.current = e.clientX;
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
    scheduleResume();
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!containerRef.current) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    pauseAutoScroll();
    containerRef.current.scrollLeft += e.deltaY;
    scheduleResume();
  };

  if (loading || cities.length === 0) {
    return null;
  }

  const items = [...cities, ...cities];

  return (
    <section className="city-marquee" aria-label="Cities with karaoke">
      <div
        ref={containerRef}
        className="city-marquee__viewport"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
        onMouseEnter={pauseAutoScroll}
        onMouseLeave={scheduleResume}
      >
        <div className="city-marquee__track" ref={trackRef}>
          {items.map((city, idx) => (
            <Link
              key={`${city}-${idx}`}
              to={`/city/${slugifyCity(city)}`}
              className="city-marquee__item"
              draggable={false}
            >
              <span className="city-marquee__name">{city}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
